import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { reportAPI } from '../services/api';

const Analytics = () => {
  const { tenant, user } = useAuth();
  const navigate = useNavigate();
  const primary = tenant?.branding?.primary_color || '#1B6B3A';
  const tenantName = tenant?.name || 'PDIC';

  const [subdist, setSubdist] = useState([]);
  const [breakdown, setBreakdown] = useState(null);
  const [trend, setTrend] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dateRange, setDateRange] = useState({
    start: new Date(new Date().setDate(new Date().getDate()-30)).toISOString().split('T')[0],
    end: new Date().toISOString().split('T')[0]
  });

  const fetchData = () => {
    setLoading(true);
    Promise.all([
      fetch(`/api/reports/subdist-analytics?start_date=${dateRange.start}&end_date=${dateRange.end}`, { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } }).then(r=>r.json()),
      fetch('/api/reports/breakdown-analytics', { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } }).then(r=>r.json()),
      fetch('/api/reports/trend-analytics?days=30', { headers: { Authorization: 'Bearer ' + localStorage.getItem('token') } }).then(r=>r.json()),
    ]).then(([sd, bd, tr]) => {
      if (sd.success) setSubdist(sd.data || []);
      if (bd.success) setBreakdown(bd);
      if (tr.success) setTrend(tr.data || []);
    }).finally(() => setLoading(false));
  };

  useEffect(() => { fetchData(); }, []);

  const totalTickets = subdist.reduce((a,s)=>a+s.total,0);
  const totalOpen = subdist.reduce((a,s)=>a+s.open,0);
  const totalEscalated = subdist.reduce((a,s)=>a+s.escalated,0);
  const avgRes = subdist.filter(s=>s.avg_resolution_hours>0).reduce((a,s,_,arr)=>a+s.avg_resolution_hours/arr.length,0);
  const maxTotal = Math.max(...subdist.map(s=>s.total),1);
  const trendMax = Math.max(...trend.map(t=>t.count),1);

  const statusColors = { open:'#f59e0b', closed:'#10b981', escalated:'#ef4444', resolved:'#6366f1' };
  const sourceLabels = { staff:'Staff', public_url:'Public/Online', whatsapp:'WhatsApp', mobile_app:'Mobile App' };
  const sourceColors = { staff:'#6366f1', public_url:'#0ea5e9', whatsapp:'#22c55e', mobile_app:'#f59e0b' };

  if (loading) return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'60vh', flexDirection:'column', gap:'16px' }}>
      <div style={{ width:'48px', height:'48px', border:`4px solid ${primary}30`, borderTop:`4px solid ${primary}`, borderRadius:'50%', animation:'spin 1s linear infinite' }} />
      <p style={{ color:'#64748b', fontSize:'14px' }}>Loading analytics...</p>
      <style>{`@keyframes spin{to{transform:rotate(360deg)}}`}</style>
    </div>
  );

  return (
    <div style={{ fontFamily:'-apple-system,BlinkMacSystemFont,"Segoe UI",sans-serif' }}>
      {/* Header */}
      <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'24px', flexWrap:'wrap', gap:'16px' }}>
        <div>
          <h1 style={{ margin:'0 0 4px', fontSize:'24px', fontWeight:'700', color:'#0f172a' }}>{tenantName} Analytics</h1>
          <p style={{ margin:0, color:'#64748b', fontSize:'14px' }}>Ticket intelligence & sub-distribution performance</p>
        </div>
        <div style={{ display:'flex', gap:'10px', alignItems:'center', flexWrap:'wrap' }}>
          <input type="date" value={dateRange.start} onChange={e=>setDateRange({...dateRange,start:e.target.value})} style={{ padding:'8px 12px', border:'1px solid #e2e8f0', borderRadius:'8px', fontSize:'13px' }} />
          <span style={{ color:'#94a3b8', fontSize:'13px' }}>to</span>
          <input type="date" value={dateRange.end} onChange={e=>setDateRange({...dateRange,end:e.target.value})} style={{ padding:'8px 12px', border:'1px solid #e2e8f0', borderRadius:'8px', fontSize:'13px' }} />
          <button onClick={fetchData} style={{ padding:'8px 20px', background:primary, color:'white', border:'none', borderRadius:'8px', fontSize:'13px', fontWeight:'600', cursor:'pointer' }}>Apply</button>
        </div>
      </div>

      {/* KPI Cards */}
      <div style={{ display:'grid', gridTemplateColumns:'repeat(4,1fr)', gap:'16px', marginBottom:'24px' }}>
        {[
          { label:'Total Tickets', value:totalTickets, icon:'🎫', color:'#6366f1', bg:'#eef2ff' },
          { label:'Open / Pending', value:totalOpen, icon:'⏳', color:'#f59e0b', bg:'#fffbeb' },
          { label:'Escalated', value:totalEscalated, icon:'🚨', color:'#ef4444', bg:'#fef2f2' },
          { label:'Avg Resolution', value:avgRes > 0 ? avgRes.toFixed(1)+'h' : 'N/A', icon:'⏱️', color:'#10b981', bg:'#ecfdf5' },
        ].map((k,i) => (
          <div key={i} style={{ background:'white', borderRadius:'16px', padding:'20px', boxShadow:'0 1px 3px rgba(0,0,0,0.06)', border:'1px solid #f1f5f9' }}>
            <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start' }}>
              <div>
                <p style={{ margin:'0 0 8px', fontSize:'13px', color:'#64748b', fontWeight:'500' }}>{k.label}</p>
                <p style={{ margin:0, fontSize:'28px', fontWeight:'800', color:k.color }}>{k.value}</p>
              </div>
              <div style={{ width:'44px', height:'44px', background:k.bg, borderRadius:'12px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'20px' }}>{k.icon}</div>
            </div>
          </div>
        ))}
      </div>

      <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:'20px', marginBottom:'20px' }}>
        {/* Trend Chart */}
        <div style={{ background:'white', borderRadius:'16px', padding:'24px', boxShadow:'0 1px 3px rgba(0,0,0,0.06)', border:'1px solid #f1f5f9' }}>
          <h3 style={{ margin:'0 0 20px', fontSize:'15px', fontWeight:'700', color:'#0f172a' }}>📈 Ticket Trend (Last 30 Days)</h3>
          {trend.length === 0 ? (
            <p style={{ color:'#94a3b8', textAlign:'center', padding:'40px 0' }}>No data for this period</p>
          ) : (
            <div style={{ display:'flex', alignItems:'flex-end', gap:'4px', height:'160px' }}>
              {trend.map((d,i) => (
                <div key={i} style={{ flex:1, display:'flex', flexDirection:'column', alignItems:'center', gap:'4px' }}>
                  <div style={{ width:'100%', display:'flex', flexDirection:'column', justifyContent:'flex-end', height:'140px', gap:'2px' }}>
                    <div title={`Closed: ${d.closed}`} style={{ width:'100%', height:`${(d.closed/trendMax)*130}px`, background:'#10b981', borderRadius:'3px 3px 0 0', minHeight: d.closed>0?'4px':'0' }} />
                    <div title={`Open: ${d.count-d.closed}`} style={{ width:'100%', height:`${((d.count-d.closed)/trendMax)*130}px`, background:`${primary}`, borderRadius:'3px 3px 0 0', minHeight:(d.count-d.closed)>0?'4px':'0' }} />
                  </div>
                  {i % 5 === 0 && <div style={{ fontSize:'9px', color:'#94a3b8', transform:'rotate(-45deg)', whiteSpace:'nowrap' }}>{d._id?.slice(5)}</div>}
                </div>
              ))}
            </div>
          )}
          <div style={{ display:'flex', gap:'16px', marginTop:'12px' }}>
            <div style={{ display:'flex', alignItems:'center', gap:'6px' }}><div style={{ width:'12px', height:'12px', background:primary, borderRadius:'3px' }}/><span style={{ fontSize:'12px', color:'#64748b' }}>Open</span></div>
            <div style={{ display:'flex', alignItems:'center', gap:'6px' }}><div style={{ width:'12px', height:'12px', background:'#10b981', borderRadius:'3px' }}/><span style={{ fontSize:'12px', color:'#64748b' }}>Closed</span></div>
          </div>
        </div>

        {/* Source & Category */}
        <div style={{ background:'white', borderRadius:'16px', padding:'24px', boxShadow:'0 1px 3px rgba(0,0,0,0.06)', border:'1px solid #f1f5f9' }}>
          <h3 style={{ margin:'0 0 16px', fontSize:'15px', fontWeight:'700', color:'#0f172a' }}>📊 Complaint Sources</h3>
          {breakdown?.by_source?.map((s,i) => (
            <div key={i} style={{ marginBottom:'12px' }}>
              <div style={{ display:'flex', justifyContent:'space-between', marginBottom:'4px' }}>
                <span style={{ fontSize:'13px', color:'#475569', fontWeight:'500' }}>{sourceLabels[s._id]||s._id||'Unknown'}</span>
                <span style={{ fontSize:'13px', fontWeight:'700', color:sourceColors[s._id]||'#6366f1' }}>{s.count}</span>
              </div>
              <div style={{ height:'8px', background:'#f1f5f9', borderRadius:'4px', overflow:'hidden' }}>
                <div style={{ height:'100%', width:`${(s.count/Math.max(...(breakdown?.by_source||[]).map(x=>x.count),1))*100}%`, background:sourceColors[s._id]||'#6366f1', borderRadius:'4px', transition:'width .5s' }} />
              </div>
            </div>
          ))}
          <h3 style={{ margin:'20px 0 12px', fontSize:'15px', fontWeight:'700', color:'#0f172a' }}>🔧 Top Issue Types</h3>
          {breakdown?.by_category?.slice(0,4).map((c,i) => (
            <div key={i} style={{ display:'flex', justifyContent:'space-between', alignItems:'center', padding:'8px 0', borderBottom:'1px solid #f8fafc' }}>
              <span style={{ fontSize:'13px', color:'#475569' }}>{c._id||'Unknown'}</span>
              <span style={{ fontSize:'13px', fontWeight:'700', color:primary, background:`${primary}15`, padding:'2px 10px', borderRadius:'20px' }}>{c.count}</span>
            </div>
          ))}
        </div>
      </div>

      {/* Sub-Dist Performance Table */}
      <div style={{ background:'white', borderRadius:'16px', padding:'24px', boxShadow:'0 1px 3px rgba(0,0,0,0.06)', border:'1px solid #f1f5f9', marginBottom:'20px' }}>
        <div style={{ display:'flex', justifyContent:'space-between', alignItems:'center', marginBottom:'20px' }}>
          <h3 style={{ margin:0, fontSize:'15px', fontWeight:'700', color:'#0f172a' }}>🏢 Sub-Distribution Performance</h3>
          <span style={{ fontSize:'12px', color:'#94a3b8' }}>{dateRange.start} → {dateRange.end}</span>
        </div>
        {subdist.length === 0 ? (
          <p style={{ color:'#94a3b8', textAlign:'center', padding:'40px 0' }}>No data for selected period</p>
        ) : (
          <table style={{ width:'100%', borderCollapse:'collapse' }}>
            <thead>
              <tr style={{ background:'#f8fafc' }}>
                {['Sub-Dist','Name','Total','Open','Closed','Escalated','Avg Resolution','Top Issue','Resolution %'].map(h=>(
                  <th key={h} style={{ padding:'12px', textAlign:'left', fontSize:'12px', color:'#64748b', fontWeight:'600', borderBottom:'2px solid #e2e8f0' }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {subdist.map((s,i) => (
                <tr key={i} onClick={()=>navigate('/tickets?sub_dist='+s.code)} style={{ borderBottom:'1px solid #f1f5f9', cursor:'pointer' }}
                  onMouseEnter={e=>e.currentTarget.style.background='#f8fafc'}
                  onMouseLeave={e=>e.currentTarget.style.background='white'}>
                  <td style={{ padding:'14px 12px' }}>
                    <span style={{ background:`${primary}15`, color:primary, padding:'3px 10px', borderRadius:'20px', fontSize:'12px', fontWeight:'700' }}>{s.code}</span>
                  </td>
                  <td style={{ padding:'14px 12px', fontSize:'13px', fontWeight:'600', color:'#1e293b' }}>{s.name}</td>
                  <td style={{ padding:'14px 12px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                      <div style={{ width:'80px', height:'6px', background:'#f1f5f9', borderRadius:'3px', overflow:'hidden' }}>
                        <div style={{ height:'100%', width:`${(s.total/maxTotal)*100}%`, background:primary, borderRadius:'3px' }} />
                      </div>
                      <span style={{ fontSize:'14px', fontWeight:'700', color:'#0f172a' }}>{s.total}</span>
                    </div>
                  </td>
                  <td style={{ padding:'14px 12px' }}><span style={{ color:'#f59e0b', fontWeight:'700', fontSize:'14px' }}>{s.open}</span></td>
                  <td style={{ padding:'14px 12px' }}><span style={{ color:'#10b981', fontWeight:'700', fontSize:'14px' }}>{s.closed}</span></td>
                  <td style={{ padding:'14px 12px' }}><span style={{ color:'#ef4444', fontWeight:'700', fontSize:'14px' }}>{s.escalated}</span></td>
                  <td style={{ padding:'14px 12px', fontSize:'13px', color:'#475569' }}>{s.avg_resolution_hours > 0 ? s.avg_resolution_hours+'h' : '—'}</td>
                  <td style={{ padding:'14px 12px', fontSize:'12px', color:'#64748b', maxWidth:'120px', overflow:'hidden', textOverflow:'ellipsis', whiteSpace:'nowrap' }}>{s.top_issue}</td>
                  <td style={{ padding:'14px 12px' }}>
                    <div style={{ display:'flex', alignItems:'center', gap:'8px' }}>
                      <div style={{ width:'50px', height:'6px', background:'#f1f5f9', borderRadius:'3px', overflow:'hidden' }}>
                        <div style={{ height:'100%', width:`${s.resolution_rate}%`, background: s.resolution_rate>=80?'#10b981':s.resolution_rate>=50?'#f59e0b':'#ef4444', borderRadius:'3px' }} />
                      </div>
                      <span style={{ fontSize:'13px', fontWeight:'700', color: s.resolution_rate>=80?'#10b981':s.resolution_rate>=50?'#f59e0b':'#ef4444' }}>{s.resolution_rate}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {/* Technician Performance */}
      {breakdown?.tech_performance?.length > 0 && (
        <div style={{ background:'white', borderRadius:'16px', padding:'24px', boxShadow:'0 1px 3px rgba(0,0,0,0.06)', border:'1px solid #f1f5f9' }}>
          <h3 style={{ margin:'0 0 20px', fontSize:'15px', fontWeight:'700', color:'#0f172a' }}>👥 Technician Performance (Last 30 Days)</h3>
          <div style={{ display:'grid', gridTemplateColumns:'repeat(auto-fill,minmax(200px,1fr))', gap:'12px' }}>
            {breakdown.tech_performance.map((t,i) => (
              <div key={i} style={{ border:'1px solid #e2e8f0', borderRadius:'12px', padding:'16px' }}>
                <div style={{ display:'flex', justifyContent:'space-between', alignItems:'flex-start', marginBottom:'12px' }}>
                  <div>
                    <p style={{ margin:'0 0 2px', fontSize:'14px', fontWeight:'700', color:'#1e293b' }}>{t.name}</p>
                    <p style={{ margin:0, fontSize:'11px', color:'#94a3b8' }}>{t.role?.replace(/_/g,' ')}</p>
                  </div>
                  <div style={{ width:'36px', height:'36px', background:`${primary}15`, borderRadius:'10px', display:'flex', alignItems:'center', justifyContent:'center', fontSize:'16px' }}>👷</div>
                </div>
                <div style={{ display:'flex', gap:'12px' }}>
                  <div style={{ textAlign:'center' }}>
                    <p style={{ margin:'0 0 2px', fontSize:'18px', fontWeight:'800', color:primary }}>{t.total}</p>
                    <p style={{ margin:0, fontSize:'10px', color:'#94a3b8' }}>Total</p>
                  </div>
                  <div style={{ textAlign:'center' }}>
                    <p style={{ margin:'0 0 2px', fontSize:'18px', fontWeight:'800', color:'#10b981' }}>{t.closed}</p>
                    <p style={{ margin:0, fontSize:'10px', color:'#94a3b8' }}>Closed</p>
                  </div>
                  <div style={{ textAlign:'center' }}>
                    <p style={{ margin:'0 0 2px', fontSize:'18px', fontWeight:'800', color: t.rate>=80?'#10b981':t.rate>=50?'#f59e0b':'#ef4444' }}>{t.rate}%</p>
                    <p style={{ margin:0, fontSize:'10px', color:'#94a3b8' }}>Rate</p>
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default Analytics;
